/** @file Allocates pose buffers sized for a skeleton, initialized to the setup pose. */
import { POSE_LAYOUT, type Pose } from '../contracts/pose';
import type { SkeletonData, SkinData } from '../skeletonData';
import { deformSize } from './deformSize';
import { resetPose } from './resetPose';

/** Used when `data.defaultSkin` names no skin: every setup placeholder resolves to no attachment. */
const NO_SKIN: SkinData = { name: '', map: new Map() };

/** `count` identity affines, WORLD_STRIDE numbers each. */
function identities(count: number): Float64Array {
  const stride = POSE_LAYOUT.WORLD_STRIDE;
  const out = new Float64Array(count * stride);
  for (let i = 0; i < count; i++) {
    out[i * stride] = 1;
    out[i * stride + 3] = 1;
  }
  return out;
}

/**
 * Allocates all pose buffers for data and resets them to setup. Attachments resolve through the default skin;
 * `deform` has one buffer per attachment index (length deformSize, 0 for regions). `world` and `slotMount` start
 * as identities and `slotBoneOverride` as -1: run updatePose for the setup world. The only allocating pose call.
 */
export function createPose(data: SkeletonData): Pose {
  const { LOCAL_STRIDE, IK_STRIDE } = POSE_LAYOUT;
  const bones = data.bones.length;
  const slots = data.slots.length;
  const pose: Pose = {
    local: new Float64Array(bones * LOCAL_STRIDE),
    applied: new Float64Array(bones * LOCAL_STRIDE),
    world: identities(bones),
    inherit: new Uint8Array(bones),
    slotColor: new Uint32Array(slots),
    slotDark: new Uint32Array(slots),
    slotAttachment: new Int32Array(slots).fill(-1),
    slotBoneOverride: new Int32Array(slots).fill(-1),
    slotMount: identities(slots),
    slotMountActive: new Uint8Array(slots),
    drawOrder: new Int32Array(slots),
    ik: new Float64Array(data.ik.length * IK_STRIDE),
    deform: data.attachments.map((attachment) => new Float64Array(deformSize(attachment))),
    flipX: false,
  };
  resetPose(pose, data, data.skins[data.defaultSkin] ?? NO_SKIN);
  return pose;
}
